import { cn } from '@/lib/utils';
import * as DropdownMenuPrimitive from '@radix-ui/react-dropdown-menu';
import { type VariantProps, cva } from 'class-variance-authority';
import { Check, ChevronRight, Circle } from 'lucide-react';
import * as React from 'react';

import {
    FluidMenuBody,
    useFluidMenuItem,
    useFluidMenuSurface
} from './fluid-menu';

const menuItemClassName =
    'relative flex items-center gap-2.5 rounded-lg px-2.5 py-2.5 text-[length:var(--fs-title-compact)] leading-[var(--lh-title-compact)] font-medium outline-hidden select-none cursor-pointer [&_svg]:size-[1.0625rem] [&_svg]:[stroke-width:var(--icon-stroke)] [&_svg]:pointer-events-none [&_svg]:shrink-0 data-disabled:opacity-50 data-disabled:pointer-events-none';

const DropdownMenu = DropdownMenuPrimitive.Root;

const DropdownMenuTrigger = DropdownMenuPrimitive.Trigger;

const DropdownMenuGroup = DropdownMenuPrimitive.Group;

const DropdownMenuPortal = DropdownMenuPrimitive.Portal;

const DropdownMenuSub = DropdownMenuPrimitive.Sub;

const DropdownMenuRadioGroup = DropdownMenuPrimitive.RadioGroup;

const DropdownMenuSubTrigger = React.forwardRef<
    React.ElementRef<typeof DropdownMenuPrimitive.SubTrigger>,
    React.ComponentPropsWithoutRef<typeof DropdownMenuPrimitive.SubTrigger> & {
        inset?: boolean;
        hideChevron?: boolean;
    }
>(
    (
        { className, inset, hideChevron, children, onFocus, ...props },
        forwardedRef
    ) => {
        const item = useFluidMenuItem<HTMLDivElement>(forwardedRef, onFocus);
        return (
            <DropdownMenuPrimitive.SubTrigger
                {...props}
                {...item}
                className={cn(
                    cn(menuItemClassName, 'data-[state=open]:bg-accent'),
                    inset && 'pl-8',
                    className
                )}
            >
                {children}
                {!hideChevron && <ChevronRight className="ml-auto" />}
            </DropdownMenuPrimitive.SubTrigger>
        );
    }
);
DropdownMenuSubTrigger.displayName =
    DropdownMenuPrimitive.SubTrigger.displayName;

const DropdownMenuSubContent = React.forwardRef<
    React.ElementRef<typeof DropdownMenuPrimitive.SubContent>,
    React.ComponentPropsWithoutRef<typeof DropdownMenuPrimitive.SubContent>
>(
    (
        { className, children, onMouseEnter, onMouseLeave, onFocus, ...props },
        forwardedRef
    ) => {
        const { hover, surfaceProps } = useFluidMenuSurface<HTMLDivElement>(
            forwardedRef,
            { onMouseEnter, onMouseLeave, onFocus }
        );
        return (
            <DropdownMenuPrimitive.SubContent
                {...props}
                {...surfaceProps}
                className={cn(
                    'relative data-[side=left]:slide-in-from-right-2 data-[side=top]:slide-in-from-bottom-2 z-50 bg-background data-[side=bottom]:slide-in-from-top-2 data-[side=right]:slide-in-from-left-2 shadow-lg p-1.5 border rounded-xl w-56 overflow-hidden text-popover-foreground data-[state=closed]:animate-out data-[state=open]:animate-in data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95',
                    className
                )}
            >
                <FluidMenuBody hover={hover}>{children}</FluidMenuBody>
            </DropdownMenuPrimitive.SubContent>
        );
    }
);
DropdownMenuSubContent.displayName =
    DropdownMenuPrimitive.SubContent.displayName;

const DropdownMenuContent = React.forwardRef<
    React.ElementRef<typeof DropdownMenuPrimitive.Content>,
    React.ComponentPropsWithoutRef<typeof DropdownMenuPrimitive.Content>
>(
    (
        {
            className,
            sideOffset = 4,
            children,
            onMouseEnter,
            onMouseLeave,
            onFocus,
            ...props
        },
        forwardedRef
    ) => {
        const { hover, surfaceProps } = useFluidMenuSurface<HTMLDivElement>(
            forwardedRef,
            { onMouseEnter, onMouseLeave, onFocus }
        );
        return (
            <DropdownMenuPortal container={window.cl_shadowRootElement}>
                <DropdownMenuPrimitive.Content
                    {...props}
                    {...surfaceProps}
                    sideOffset={sideOffset}
                    className={cn(
                        'relative data-[side=left]:slide-in-from-right-2 data-[side=top]:slide-in-from-bottom-2 z-50 bg-background data-[side=bottom]:slide-in-from-top-2 data-[side=right]:slide-in-from-left-2 shadow-md p-1.5 border rounded-xl min-w-[calc(100%+1rem)] overflow-hidden text-popover-foreground data-[state=closed]:animate-out data-[state=open]:animate-in data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95',
                        className
                    )}
                >
                    <FluidMenuBody hover={hover}>{children}</FluidMenuBody>
                </DropdownMenuPrimitive.Content>
            </DropdownMenuPortal>
        );
    }
);
DropdownMenuContent.displayName = DropdownMenuPrimitive.Content.displayName;

const dropdownMenuItemVariants = cva(menuItemClassName, {
    variants: {
        variant: {
            default: 'focus:text-accent-foreground',
            destructive: 'text-destructive [&_svg]:text-destructive'
        }
    },
    defaultVariants: {
        variant: 'default'
    }
});

const DropdownMenuItem = React.forwardRef<
    React.ElementRef<typeof DropdownMenuPrimitive.Item>,
    React.ComponentPropsWithoutRef<typeof DropdownMenuPrimitive.Item> &
        VariantProps<typeof dropdownMenuItemVariants> & {
            inset?: boolean;
            highlight?: boolean;
        }
>(
    (
        { className, inset, variant, onFocus, highlight, ...props },
        forwardedRef
    ) => {
        const item = useFluidMenuItem<HTMLDivElement>(
            forwardedRef,
            onFocus,
            highlight
        );
        return (
            <DropdownMenuPrimitive.Item
                {...props}
                {...item}
                data-destructive={variant === 'destructive' ? '' : undefined}
                className={cn(
                    dropdownMenuItemVariants({ variant }),
                    inset && 'pl-8',
                    className
                )}
            />
        );
    }
);
DropdownMenuItem.displayName = DropdownMenuPrimitive.Item.displayName;

const DropdownMenuCheckboxItem = React.forwardRef<
    React.ElementRef<typeof DropdownMenuPrimitive.CheckboxItem>,
    React.ComponentPropsWithoutRef<typeof DropdownMenuPrimitive.CheckboxItem>
>(({ className, children, checked, onFocus, ...props }, forwardedRef) => {
    const item = useFluidMenuItem<HTMLDivElement>(forwardedRef, onFocus);
    return (
        <DropdownMenuPrimitive.CheckboxItem
            {...props}
            {...item}
            className={cn(menuItemClassName, 'pl-8', className)}
            checked={checked}
        >
            <span className="left-2 absolute flex justify-center items-center w-3.5 h-3.5">
                <DropdownMenuPrimitive.ItemIndicator>
                    <Check className="w-4 h-4" />
                </DropdownMenuPrimitive.ItemIndicator>
            </span>
            {children}
        </DropdownMenuPrimitive.CheckboxItem>
    );
});
DropdownMenuCheckboxItem.displayName =
    DropdownMenuPrimitive.CheckboxItem.displayName;

const DropdownMenuRadioItem = React.forwardRef<
    React.ElementRef<typeof DropdownMenuPrimitive.RadioItem>,
    React.ComponentPropsWithoutRef<typeof DropdownMenuPrimitive.RadioItem>
>(({ className, children, onFocus, ...props }, forwardedRef) => {
    const item = useFluidMenuItem<HTMLDivElement>(forwardedRef, onFocus);
    return (
        <DropdownMenuPrimitive.RadioItem
            {...props}
            {...item}
            className={cn(menuItemClassName, 'pl-8', className)}
        >
            <span className="left-2 absolute flex justify-center items-center w-3.5 h-3.5">
                <DropdownMenuPrimitive.ItemIndicator>
                    <Circle className="fill-current w-2 h-2" />
                </DropdownMenuPrimitive.ItemIndicator>
            </span>
            {children}
        </DropdownMenuPrimitive.RadioItem>
    );
});
DropdownMenuRadioItem.displayName = DropdownMenuPrimitive.RadioItem.displayName;

const DropdownMenuLabel = React.forwardRef<
    React.ElementRef<typeof DropdownMenuPrimitive.Label>,
    React.ComponentPropsWithoutRef<typeof DropdownMenuPrimitive.Label> & {
        inset?: boolean;
    }
>(({ className, inset, ...props }, ref) => (
    <DropdownMenuPrimitive.Label
        ref={ref}
        className={cn(
            'px-2 py-1.5 font-semibold text-sm',
            inset && 'pl-8',
            className
        )}
        {...props}
    />
));
DropdownMenuLabel.displayName = DropdownMenuPrimitive.Label.displayName;

const DropdownMenuSeparator = React.forwardRef<
    React.ElementRef<typeof DropdownMenuPrimitive.Separator>,
    React.ComponentPropsWithoutRef<typeof DropdownMenuPrimitive.Separator>
>(({ className, ...props }, ref) => (
    <DropdownMenuPrimitive.Separator
        ref={ref}
        className={cn('bg-muted -mx-1 my-1 h-px', className)}
        {...props}
    />
));
DropdownMenuSeparator.displayName = DropdownMenuPrimitive.Separator.displayName;

const DropdownMenuShortcut = ({
    className,
    ...props
}: React.HTMLAttributes<HTMLSpanElement>) => {
    return (
        <span
            className={cn(
                'opacity-60 ml-auto text-sm tracking-widest',
                className
            )}
            {...props}
        />
    );
};
DropdownMenuShortcut.displayName = 'DropdownMenuShortcut';

export {
    menuItemClassName,
    DropdownMenu,
    DropdownMenuTrigger,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuCheckboxItem,
    DropdownMenuRadioItem,
    DropdownMenuLabel,
    DropdownMenuSeparator,
    DropdownMenuShortcut,
    DropdownMenuGroup,
    DropdownMenuPortal,
    DropdownMenuSub,
    DropdownMenuSubContent,
    DropdownMenuSubTrigger,
    DropdownMenuRadioGroup
};
